<template>
  <!-- The sections of the resume, as icons with a line after each: a slim column beside the form on
       wide screens, a row above it on small ones. A tooltip names each one. Picking one shows its form. -->
  <nav aria-label="Resume sections" @scroll.passive="hideTip"
    class="flex md:flex-col items-center gap-1 p-2 md:px-0 md:py-2 overflow-x-auto md:overflow-x-visible md:overflow-y-auto md:w-14 md:flex-shrink-0 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128]">
    <template v-for="item in items" :key="item.key">
      <button type="button" @click="$emit('select', item.key)"
        @mouseenter="showTip($event, item)" @mouseleave="hideTip" @focus="showTip($event, item)" @blur="hideTip"
        :aria-label="item.tip" :aria-current="active === item.key ? 'true' : undefined"
        class="relative flex-shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
        :class="active === item.key
          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300'
          : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-100'">
        <svg class="w-[18px] h-[18px]" fill="none" stroke="currentColor" stroke-width="1.6" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" :d="item.icon"/>
        </svg>
        <span v-if="item.count" class="absolute top-0.5 right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-blue-600 text-white text-[9px] font-bold leading-[15px] text-center tabular-nums" aria-hidden="true">{{ item.count }}</span>
      </button>
      <!-- A line after each one: edge to edge in the column, upright in the row -->
      <hr class="flex-shrink-0 border-0 bg-gray-200 dark:bg-gray-700 w-px h-6 mx-0.5 md:w-full md:h-px md:mx-0 md:self-stretch" aria-hidden="true">
    </template>

    <button v-if="canAdd" ref="addButton" type="button" @click="startAdding" @mouseenter="showTip($event, { tip: 'Add a section' })" @mouseleave="hideTip" @focus="showTip($event, { tip: 'Add a section' })" @blur="hideTip"
      aria-label="Add a section" aria-haspopup="dialog" :aria-expanded="adding"
      class="flex-shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50">
      <svg class="w-[18px] h-[18px]" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" :d="ICONS.add"/></svg>
    </button>
  </nav>

  <!-- Both float over the page, so a column that scrolls cannot clip them -->
  <Teleport to="body">
    <div v-if="tip" role="tooltip" class="fixed z-50 px-2 py-1 rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs font-medium whitespace-nowrap shadow-lg pointer-events-none" :style="tip.style">{{ tip.text }}</div>

    <form v-if="adding" role="dialog" aria-label="Add a section" @submit.prevent="submit" @keydown.esc="adding = false"
      class="fixed z-50 flex items-center gap-1.5 p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shadow-lg" :style="popoverStyle">
      <input ref="input" v-model="name" type="text" maxlength="80" placeholder="Section name, for example Awards" aria-label="New section name"
        class="w-56 h-8 px-2.5 text-[13px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400">
      <button type="submit" :disabled="!name.trim()" class="h-8 px-3 rounded-lg text-[13px] font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">Add</button>
    </form>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { SECTION_LABELS } from './sectionDefs.js'
import { SECTION_ICONS as ICONS } from './sectionIcons.js'
import { DESIGN_KEY, ORDER_KEY, sectionKeys } from './sectionOrder.js'

const props = defineProps({
  content: { type: Object, required: true },
  active: { type: String, required: true },
  canAdd: { type: Boolean, default: true },
})
const emit = defineEmits(['select', 'add'])

const entry = (key, label, icon, count = 0) => ({ key, icon, count, tip: count ? `${label} (${count})` : label })

function sectionEntry(key) {
  if (key === 'summary') return entry(key, SECTION_LABELS.summary, ICONS.summary)
  const custom = props.content.custom.find((section) => section.id === key)
  if (custom) return entry(key, custom.title || 'Untitled section', ICONS.custom, custom.entries.length)
  return entry(key, SECTION_LABELS[key], ICONS[key], props.content[key].length)
}

// Personal details first, then the sections in the order the resume shows them, then the Section order form that arranges and hides them, and the Design form (template and look).
const items = computed(() => [
  entry('basics', 'Personal details', ICONS.basics),
  ...sectionKeys(props.content).map(sectionEntry),
  entry(ORDER_KEY, 'Section order', ICONS.order),
  entry(DESIGN_KEY, 'Design', ICONS.design),
])

// Tooltip: beside the button in the column, under it in the row.
const tip = ref(null)
const isColumn = () => window.matchMedia?.('(min-width: 768px)').matches

function placeAt(target) {
  const rect = target.getBoundingClientRect()
  return isColumn()
    ? { left: `${rect.right + 8}px`, top: `${rect.top + rect.height / 2}px`, transform: 'translateY(-50%)' }
    : { left: `${rect.left + rect.width / 2}px`, top: `${rect.bottom + 6}px`, transform: 'translateX(-50%)' }
}

function showTip(event, item) {
  if (adding.value) return
  tip.value = { text: item.tip, style: placeAt(event.currentTarget) }
}
const hideTip = () => { tip.value = null }

// Adding a section asks for its name in a small box next to the button.
const adding = ref(false)
const name = ref('')
const input = ref(null)
const addButton = ref(null)
const popoverStyle = ref({})

function startAdding() {
  hideTip()
  name.value = ''
  const rect = addButton.value.getBoundingClientRect()
  popoverStyle.value = isColumn()
    ? { left: `${rect.right + 8}px`, top: `${Math.max(8, rect.top - 6)}px` }
    : { left: `${Math.max(8, rect.left - 120)}px`, top: `${rect.bottom + 8}px` }
  adding.value = true
  nextTick(() => input.value?.focus())
}

function submit() {
  const title = name.value.trim()
  adding.value = false
  if (title) emit('add', title)
}

// A click anywhere else closes the box.
function onOutside(event) {
  if (!event.target.closest?.('form[aria-label="Add a section"]') && !addButton.value?.contains(event.target)) adding.value = false
}
watch(adding, (isOpen) => {
  if (isOpen) document.addEventListener('pointerdown', onOutside)
  else document.removeEventListener('pointerdown', onOutside)
})
onBeforeUnmount(() => document.removeEventListener('pointerdown', onOutside))
</script>
